// ─── OCR misurazioni da foto/screen ─────────────────────────────────────────
// Comprime un'immagine lato client e chiede a Haiku 4.5 di leggere i valori di
// composizione corporea (peso, grasso, BMR, ...) mappandoli ai 13 campi DB di
// body_measurements. Ritorna SOLO i campi letti; niente invenzioni. L'immagine
// è transiente: non viene salvata su DB né storage. Vedi spec
// docs/superpowers/specs/2026-07-25-measurement-photo-ocr-design.md
import { api, cj, fixJson, HAIKU } from './core.js';

// Whitelist dei campi DB accettati. Chiavi extra dal modello vengono scartate.
const FIELD_KEYS = [
  'altezza_cm', 'peso_kg', 'grasso_pct', 'massa_muscolare_kg', 'massa_ossea_kg',
  'liquidi_pct', 'grasso_addominale', 'bmr_kcal', 'proteine_pct',
  'muscolo_scheletrico_pct', 'grasso_sottocutaneo_pct', 'massa_magra_kg',
  'indice_corporeo',
];

const MAX_EDGE = 1500;                 // lato lungo massimo (px)
const TARGET_B64_BYTES = 700 * 1024;   // budget base64 (proxy limit 800KB)

// compressImage(file) → { dataB64, mediaType }.
// Ridimensiona il lato lungo a MAX_EDGE, esporta JPEG. Se il base64 supera il
// budget, riduce qualità/dimensione (max 4 tentativi). Output sempre JPEG.
export async function compressImage(file) {
  const bitmap = await createImageBitmap(file);
  let edge = MAX_EDGE;
  let quality = 0.72;
  try {
    for (let attempt = 0; attempt < 4; attempt++) {
      const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
      const dataB64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
      if (dataB64.length <= TARGET_B64_BYTES || attempt === 3) {
        return { dataB64, mediaType: 'image/jpeg' };
      }
      quality -= 0.15;
      if (quality < 0.4) { quality = 0.6; edge = Math.round(edge * 0.8); }
    }
  } finally {
    bitmap.close && bitmap.close();
  }
}

const OCR_SYSTEM =
  'Sei un estrattore di dati. Leggi SOLO i valori chiaramente visibili ' +
  'nell\'immagine di una bilancia smart o app di composizione corporea. ' +
  'Non inventare, non stimare. Rispondi esclusivamente con un oggetto JSON.';

function buildPrompt() {
  return [
    'Questa è la schermata di un\'app di bilancia smart (composizione corporea).',
    '',
    'LAYOUT — leggi con MOLTA attenzione la disposizione:',
    '- La schermata è una griglia, spesso a DUE COLONNE.',
    '- In OGNI cella il VALORE numerico sta sulla riga SOPRA e l\'ETICHETTA testuale sulla riga SOTTO.',
    '- Associa ogni valore SOLO all\'etichetta immediatamente sottostante, nella STESSA colonna.',
    '- Non incrociare valori tra righe o colonne diverse. Righe percentuali adiacenti sono facili da scambiare: verifica ciascuna.',
    '- Leggi TUTTI i campi presenti, senza saltarne nessuno.',
    '',
    'Mappa le etichette a queste chiavi (unità tra parentesi). Fra virgolette le diciture italiane tipiche:',
    '- altezza_cm (cm) — di norma NON presente in queste schermate',
    '- peso_kg (kg) — il peso grande in alto',
    '- grasso_pct (%) — "Grasso Corporeo" / "Body Fat"',
    '- massa_muscolare_kg (kg) — "Massa Muscolare"',
    '- massa_ossea_kg (kg) — "Massa Ossea" / "Bone"',
    '- liquidi_pct (%) — "Liquidi Corporei" / "Water"',
    '- grasso_addominale (indice intero) — "Grasso Addominale" / visceral fat',
    '- bmr_kcal (kcal) — "BMR" / "BMR" / metabolismo basale',
    '- proteine_pct (%) — "Proteine" / "Protein"',
    '- muscolo_scheletrico_pct (%) — "Muscolo Scheletrico" / "Skeletal Muscle"',
    '- grasso_sottocutaneo_pct (%) — "Grasso Sottocutaneo" / "Subcutaneous Fat"',
    '- massa_magra_kg (kg) — "Massa Magra" / "Lean Mass"',
    '- indice_corporeo — "Indice Corporeo" (valore numerico, es. 94)',
    '',
    'ATTENZIONE a tre campi DISTINTI e spesso adiacenti — non confonderli:',
    '  "Grasso Corporeo" → grasso_pct;  "Grasso Sottocutaneo" → grasso_sottocutaneo_pct;  "Proteine" → proteine_pct.',
    'Leggi il numero che sta sopra CIASCUNA di queste etichette separatamente.',
    '',
    'Regole:',
    '- Includi SOLO le chiavi lette con certezza; ometti (non inventare) quelle illeggibili.',
    '- NON salvare "BMI" né "Corporatura"/"Body Type" (non sono campi richiesti): eventualmente citali in "_note".',
    '- Assumi unità metriche (kg, cm). Converti se palesemente in lb/ft.',
    '- Se leggi una data della misurazione, aggiungi "_date":"YYYY-MM-DD".',
    '- Se ci sono campi non mappabili, elencali brevemente in "_note".',
    '- Rispondi con SOLO un oggetto JSON, senza testo attorno.',
  ].join('\n');
}

// readMeasurementFromImage({dataB64, mediaType}) → { fields, dateISO, note }.
export async function readMeasurementFromImage({ dataB64, mediaType }) {
  const msgs = [{
    role: 'user',
    content: [
      { type: 'image', source: { type: 'base64', media_type: mediaType, data: dataB64 } },
      { type: 'text', text: buildPrompt() },
    ],
  }];
  const raw = await api(msgs, OCR_SYSTEM, 700, HAIKU);
  let parsed;
  try {
    parsed = JSON.parse(fixJson(cj(raw)));
  } catch {
    throw new Error('Non sono riuscito a leggere i valori dalla foto. Riprova con uno screenshot più nitido o inserisci a mano.');
  }
  const fields = {};
  for (const k of FIELD_KEYS) {
    const n = Number(parsed[k]);
    if (parsed[k] != null && Number.isFinite(n)) fields[k] = n;
  }
  const dateISO = (typeof parsed._date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(parsed._date))
    ? parsed._date : null;
  const note = (typeof parsed._note === 'string' && parsed._note.trim())
    ? parsed._note.trim() : null;
  return { fields, dateISO, note };
}
